/*
 * Copyright (c) Nexign, JSC, 2019-2020.
 */
/**
 * Expenses Analyzer Table Map Types
 *
 * @author Fedorov Maxim
 * @author Anton Panasenko
 * @author Evgenij Tyan
 * @author Aleksandr Perkhunov
 * @author Simar Mugattarov
 * @version 1.2.0
 * @since 1.1.0
 */

import {
    EXPENSE_ANALYZER_CHART_ACCOUNT,
    EXPENSE_ANALYZER_CHART_ACCOUNT_DETAILS,
    EXPENSE_ANALYZER_CHART_BILLS_ACCOUNT,
    EXPENSE_ANALYZER_CHART_BILLS_ACCOUNT_DETAILS,
    EXPENSE_ANALYZER_CHART_BILLS_SUBSCRIBER,
    EXPENSE_ANALYZER_CHART_BILLS_SUBSCRIBER_DETAILS,
    EXPENSE_ANALYZER_CHART_FIXED_DETAILS,
    EXPENSE_ANALYZER_CHART_MOBILE_DETAILS,
    EXPENSE_ANALYZER_CHART_SUBSCRIBER,
    EXPENSE_ANALYZER_CHART_SUBSCRIBER_DETAILS,
    EXPENSES_ANALYZER,
    EXPENSES_ANALYZER_ACCOUNTS_BASKET,
    EXPENSES_ANALYZER_ACCOUNTS_LINE,
    EXPENSES_ANALYZER_BILLS_ACCOUNT_BASKET,
    EXPENSES_ANALYZER_BILLS_SUBSCRIBER_BASKET,
    EXPENSES_ANALYZER_SUBSCRIBER_BASKET,
    EXPENSES_ANALYZER_SUBSCRIBER_FIXED_BASKET,
    EXPENSES_ANALYZER_SUBSCRIBER_MOBILE_BASKET,
    EXPENSES_ANALYZER_SUBSCRIBERS_BASKET,
    EXPENSES_ANALYZER_TABLE_DETAILS_ACCOUNT,
    EXPENSES_ANALYZER_TABLE_DETAILS_ACCOUNT_BILLS,
    EXPENSES_ANALYZER_TABLE_DETAILS_SUBSCRIBER,
    EXPENSES_ANALYZER_TABLE_DETAILS_SUBSCRIBER_BILLS,
} from '../../../../../modules/app/Api';
import { colors } from '../../../../../theme';
import { stringFormat } from '../../../../../utils/stringFormat';
import { EExpensesAnalyzerTypes } from '../../../types/pageExpensesAnalyzerTypes';
import { EExpensesAnalyzerField } from './expensesAnalyzerTableTypes';

type TMapExpensesAnalyzerTableColor = { [name: string]: string }

export const MapExpensesAnalyzerTableColor: TMapExpensesAnalyzerTableColor = {
    [EExpensesAnalyzerField.CHARGES]: colors.chartCostsOneTimeCharges,
    [EExpensesAnalyzerField.MONTH_CHARGE_SRLS]: colors.chartCostsOtherRecurringCharges,
    [EExpensesAnalyzerField.MONTH_CHARGE_RTPL]: colors.chartCostsRecurringChargesForRatePlan,
    [EExpensesAnalyzerField.AMOUNT_LOCAL_MACRO]:
    colors.chartCostsTrafficChargesAtHomeMacroRegionAndNotInHomeRegion,
    [EExpensesAnalyzerField.AMOUNT_LOCAL]: colors.chartCostsTrafficChargesAtHomeRegion,
    [EExpensesAnalyzerField.AMOUNT_COUNTRY_ROAMING]:
    colors.chartCostsTrafficChargesAtOutHomeMacroRegion,
    [EExpensesAnalyzerField.AMOUNT_ROAMING]: colors.chartCostsTrafficChargesAtRoaming,
};

export interface IExpensesAnalyzerApiPath {
    accountsList?: string;
    api: string;
    basket: string;
    chart: string;
    details: string;
    export: string;
    footerOrderDetail?: string;
    list: string;
    tableDetails: string;
}

interface IMapExpensesAnalyzerTypeToApiPath {
    all?: IExpensesAnalyzerApiPath;
    common: IExpensesAnalyzerApiPath;
}

/*
* -------------------------------------------
*           API PATHS
* -------------------------------------------
*/

const expensesAnalyzerAccountFromAccount = {
    all: {
        accountsList: stringFormat(EXPENSES_ANALYZER_ACCOUNTS_LINE, 'account'),
        api: `${EXPENSES_ANALYZER}/account`,
        basket: EXPENSES_ANALYZER_ACCOUNTS_BASKET,
        chart: EXPENSE_ANALYZER_CHART_ACCOUNT,
        details: EXPENSE_ANALYZER_CHART_ACCOUNT_DETAILS,
        export: `${EXPENSES_ANALYZER}/account/export/account/format/{0}`,
        list: `${EXPENSES_ANALYZER}/account/from/account`,
        tableDetails: EXPENSES_ANALYZER_TABLE_DETAILS_ACCOUNT,
    },
    common: {
        api: `${EXPENSES_ANALYZER}/subscriber`,
        basket: EXPENSES_ANALYZER_SUBSCRIBERS_BASKET,
        chart: EXPENSE_ANALYZER_CHART_SUBSCRIBER,
        details: EXPENSE_ANALYZER_CHART_ACCOUNT_DETAILS,
        export: `${EXPENSES_ANALYZER}/subscriber/export/account/format/{0}`,
        footerOrderDetail: '/callsDetail/expenses/mobile',
        list: `${EXPENSES_ANALYZER}/subscriber/from/account`,
        tableDetails: EXPENSES_ANALYZER_TABLE_DETAILS_SUBSCRIBER,
    },
};

const expensesAnalyzerAccountFromBills = {
    all: {
        accountsList: stringFormat(EXPENSES_ANALYZER_ACCOUNTS_LINE, 'bills'),
        api: `${EXPENSES_ANALYZER}/account`,
        basket: EXPENSES_ANALYZER_BILLS_ACCOUNT_BASKET,
        chart: EXPENSE_ANALYZER_CHART_BILLS_ACCOUNT,
        details: EXPENSE_ANALYZER_CHART_BILLS_ACCOUNT_DETAILS,
        export: `${EXPENSES_ANALYZER}/account/export/bills/format/{0}`,
        list: `${EXPENSES_ANALYZER}/account/from/bills`,
        tableDetails: EXPENSES_ANALYZER_TABLE_DETAILS_ACCOUNT_BILLS,
    },
    common: {
        api: `${EXPENSES_ANALYZER}/subscriber`,
        basket: EXPENSES_ANALYZER_BILLS_SUBSCRIBER_BASKET,
        chart: EXPENSE_ANALYZER_CHART_BILLS_SUBSCRIBER,
        details: EXPENSE_ANALYZER_CHART_BILLS_ACCOUNT_DETAILS,
        export: `${EXPENSES_ANALYZER}/subscriber/export/bills/format/{0}`,
        list: `${EXPENSES_ANALYZER}/subscriber/from/bills`,
        tableDetails: EXPENSES_ANALYZER_TABLE_DETAILS_SUBSCRIBER_BILLS,
    },
};

const expensesAnalyzerSubscribersFromBills = {
    common: {
        api: `${EXPENSES_ANALYZER}/subscriber`,
        basket: EXPENSES_ANALYZER_BILLS_SUBSCRIBER_BASKET,
        chart: EXPENSE_ANALYZER_CHART_BILLS_ACCOUNT,
        details: EXPENSE_ANALYZER_CHART_BILLS_SUBSCRIBER_DETAILS,
        export: `${EXPENSES_ANALYZER}/subscriber/export/bills/format/{0}`,
        list: `${EXPENSES_ANALYZER}/subscriber/from/bills`,
        tableDetails: EXPENSES_ANALYZER_TABLE_DETAILS_SUBSCRIBER_BILLS,
    },
};

const expensesAnalyzerSubscribersFromAccount = {
    common: {
        api: `${EXPENSES_ANALYZER}/subscriber`,
        basket: EXPENSES_ANALYZER_SUBSCRIBER_BASKET,
        chart: EXPENSE_ANALYZER_CHART_SUBSCRIBER,
        details: EXPENSE_ANALYZER_CHART_SUBSCRIBER_DETAILS,
        export: `${EXPENSES_ANALYZER}/subscriber/export/account/format/{0}`,
        list: `${EXPENSES_ANALYZER}/subscriber/from/account`,
        tableDetails: EXPENSES_ANALYZER_TABLE_DETAILS_SUBSCRIBER,
    },
};

const expensesAnalyzerSubscribersFromMobile = {
    common: {
        api: `${EXPENSES_ANALYZER}/subscriber`,
        basket: EXPENSES_ANALYZER_SUBSCRIBER_MOBILE_BASKET,
        chart: EXPENSE_ANALYZER_CHART_SUBSCRIBER,
        details: EXPENSE_ANALYZER_CHART_MOBILE_DETAILS,
        export: `${EXPENSES_ANALYZER}/subscriber/export/mobile/format/{0}`,
        footerOrderDetail: '/callsDetail/expenses/mobile',
        list: `${EXPENSES_ANALYZER}/subscriber/from/mobile`,
        tableDetails: EXPENSES_ANALYZER_TABLE_DETAILS_SUBSCRIBER,
    },
};

const expensesAnalyzerSubscribersFromFixed = {
    common: {
        api: `${EXPENSES_ANALYZER}/subscriber`,
        basket: EXPENSES_ANALYZER_SUBSCRIBER_FIXED_BASKET,
        chart: EXPENSE_ANALYZER_CHART_SUBSCRIBER,
        details: EXPENSE_ANALYZER_CHART_FIXED_DETAILS,
        export: `${EXPENSES_ANALYZER}/subscriber/export/fixed/format/{0}`,
        footerOrderDetail: '/callsDetail/expenses/fixed',
        list: `${EXPENSES_ANALYZER}/subscriber/from/fixed`,
        tableDetails: EXPENSES_ANALYZER_TABLE_DETAILS_SUBSCRIBER,
    },
};

/*
* -------------------------------------------
*           API PATH MAPPING
* -------------------------------------------
*/

export const MapExpensesAnalyzerTypeToApiPath: { [key: string]: IMapExpensesAnalyzerTypeToApiPath } = {
    [EExpensesAnalyzerTypes.Account]: expensesAnalyzerAccountFromAccount,
    [EExpensesAnalyzerTypes.AccountSubscribers]: expensesAnalyzerSubscribersFromAccount,
    [EExpensesAnalyzerTypes.AccountSingle]: expensesAnalyzerAccountFromAccount,
    [EExpensesAnalyzerTypes.AccountSingleSubscribers]: expensesAnalyzerSubscribersFromAccount,

    [EExpensesAnalyzerTypes.BillsAccount]: expensesAnalyzerAccountFromBills,
    [EExpensesAnalyzerTypes.BillsAccountSingle]: expensesAnalyzerAccountFromBills,
    [EExpensesAnalyzerTypes.BillsAccountsSubscribers]: expensesAnalyzerSubscribersFromBills,
    [EExpensesAnalyzerTypes.BillsAccountSingleSubscribers]: expensesAnalyzerSubscribersFromBills,

    [EExpensesAnalyzerTypes.BillsUnpaidAccount]: expensesAnalyzerAccountFromBills,
    [EExpensesAnalyzerTypes.BillsUnpaidAccountSingle]: expensesAnalyzerAccountFromBills,
    [EExpensesAnalyzerTypes.BillsUnpaidAccountsSubscribers]: expensesAnalyzerSubscribersFromBills,
    [EExpensesAnalyzerTypes.BillsUnpaidAccountSingleSubscribers]: expensesAnalyzerSubscribersFromBills,

    [EExpensesAnalyzerTypes.SubscriberFixed]: expensesAnalyzerSubscribersFromFixed,
    [EExpensesAnalyzerTypes.SubscriberFixedSingle]: expensesAnalyzerSubscribersFromFixed,
    [EExpensesAnalyzerTypes.SubscriberMobile]: expensesAnalyzerSubscribersFromMobile,
    [EExpensesAnalyzerTypes.SubscriberMobileSingle]: expensesAnalyzerSubscribersFromMobile,
};
